import * as yup from 'yup';

// Matches the design prototype's rule (and the backend's intent): a plausible
// e-mail, and a phone with at least 10 numeric digits (covers AU mobiles like
// 0412 345 678 and US numbers).
export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const digitsOf = (value?: string | null): string => (value || '').replace(/\D/g, '');

export const isAustralia = (country?: string | null): boolean => {
  const c = (country || '').toLowerCase();
  return c === 'au' || c.includes('austral');
};

/**
 * Produce a country-coded, digits-only phone string suitable for the
 * updatePhoneNumber path (the datasource prepends a single '+', so never include
 * one here). Falls back to US if the country is unknown.
 */
export const normalizePhone = (raw?: string | null, country?: string | null): string => {
  let d = digitsOf(raw);
  if (d.startsWith('00')) d = d.slice(2); // drop international call prefix

  if (isAustralia(country)) {
    if (d.startsWith('61')) return d;
    if (d.startsWith('0')) return `61${d.slice(1)}`;
    if (d.length === 9) return `61${d}`; // local without leading 0
    return d;
  }

  // US / default
  if (d.startsWith('1') && d.length === 11) return d;
  if (d.length === 10) return `1${d}`;
  return d;
};

export type PersonalInfoValues = {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  email: string;
};

export const personalInfoSchema: yup.ObjectSchema<PersonalInfoValues> = yup.object({
  firstName: yup.string().trim().required('This field is required'),
  lastName: yup.string().trim().required('This field is required'),
  email: yup
    .string()
    .trim()
    .required('This field is required')
    .matches(EMAIL_RE, 'Enter a valid email address'),
  phoneNumber: yup
    .string()
    .required('This field is required')
    // Valid = 10 to 15 digits (15 is the E.164 maximum); too short OR too long is invalid.
    .test('phone', 'Enter a valid phone number', (v) => {
      if (/[a-z]/i.test(v || '')) return false;
      const len = digitsOf(v).length;
      return len === 10;
    }),
});
